<template>
  <div>
    <div class="mb-4 d-flex align-center">
      <h2>園児・保護者情報</h2>
      <v-spacer></v-spacer>
      <v-menu offset-y>
        <template v-slot:activator="{ props }">
          <v-btn
            class="mr-2"
            color="primary"
            variant="outlined"
            :disabled="isFetching"
            v-bind="props"
          >
            <v-icon start>mdi-tray-arrow-down</v-icon>
            ダウンロード
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="pdfDialog = true">
            <v-list-item-title>案内状PDF</v-list-item-title>
          </v-list-item>
          <!--MEMORUの案内状が不要になったためコメントアウト-->
          <!--<v-list-item @click="memoruPdfDialog = true">
            <v-list-item-title>MEMORU案内状PDF</v-list-item-title>
          </v-list-item>-->     
          <v-list-item @click="exportChildrenDialog = true">
            <v-list-item-title>園児CSV</v-list-item-title>
          </v-list-item>
          <v-list-item
            href="https://s3.ap-northeast-1.amazonaws.com/data.c-c-s.app/public/csv_format/import_child_sample.csv"
          >
            <v-list-item-title>サンプルCSV</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        @click="importDialog = true"
        :disabled="isFetching"
        color="primary"
        class="mr-2"
        variant="outlined"
      >
        <v-icon start>mdi-plus</v-icon>
        一括インポート
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        to="/children/account-create"
        :disabled="isFetching"
      >
        <v-icon start>mdi-plus</v-icon>
        作成
      </v-btn>
    </div>

    <!-- 検索フォーム -->
    <v-card variant="flat" class="mt-4 pa-2" color="grey-lighten-5">
      <v-container>
        <v-row>
          <v-col cols="4">
            <h5>クラス</h5>
            <class-picker :classes="classes" v-model="classId" color="primary"></class-picker>
          </v-col>
          <v-col cols="4">
            <h5>姓</h5>
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              v-model="lastName"
              color="primary"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <h5>名</h5>
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              v-model="firstName"
              color="primary"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <h5>性別</h5>
            <gender-picker v-model="gender" color="primary"></gender-picker>
          </v-col>
          <v-col cols="4">
            <h5>年齢</h5>
            <age-picker v-model="age"></age-picker>
          </v-col>
          <v-col cols="4" align-self="center">
            <v-checkbox
              v-model="searchFilter.isQuit"
              color="primary"
              label="退園済み"
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row>
          <v-spacer></v-spacer>
          <v-btn
            @click="search"
            large
            color="primary"
            :disabled="isFetching"
            class="my-2"
            variant="flat"
            :loading="isFetching"
            >検索</v-btn
          >
          <v-spacer></v-spacer>
        </v-row>
      </v-container>
    </v-card>

    <!-- 園児一覧 -->
    <v-data-table
      class="pt-4"
      :items="children"
      v-model="selectedChildren"
      :headers="childrenTableHeaders"
      item-value="childId"
      return-object
      :items-per-page="-1"
      show-select
      :loading="isFetching"
      color="primary"
    >
      <template v-slot:loading>
        <v-skeleton-loader type="table-tbody"></v-skeleton-loader>
      </template>
      <template v-slot:item.name="{ item }">
        {{ `${item.lastName} ${item.firstName}` }}
      </template>

      <template v-slot:item.class="{ item }">
        {{ getClassName(item) }}
      </template>

      <template v-slot:item.gender="{ item }">
        {{ item.gender === "male" ? "男性" : "女性" }}
      </template>

      <template v-slot:item.age="{ item }">
        {{ getAgeBasedonBirthdate(item.dateOfBirth) + "歳" }}
      </template>

      <template v-slot:item.isFamilyAssociated="{ item }">
        <v-chip color="success" size="small" v-if="item.isFamilyAssociated">
          <v-icon start size="small">mdi-check-circle</v-icon>
          完了
        </v-chip>
        <v-chip v-else color="grey" size="small" variant="outlined">未完了</v-chip>
      </template>

      <template v-slot:item.edit="{ item }">
        <div class="d-flex justify-space-around align-center">
          <v-btn
            color="primary"
            variant="outlined"
            class="mr-2"
            size="small"
            :disabled="isFetching"
            :to="`/children/account/${item.childId}`"
          >アカウント設定</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="mr-2"
            size="small"
            :disabled="isFetching"
            :to="`/children/document/${item.childId}`"
          >児童票</v-btn>

          <v-btn
            color="#EE6190"
            theme="dark"
            variant="flat"
            size="small"
            :disabled="isFetching"
            :to="`/parent/${item.childId}/document`"
          >保護者票</v-btn>
        </div>
      </template>

      <template #bottom></template>
    </v-data-table>

    <v-dialog v-model="pdfDialog" max-width="600px">
      <v-card>
        <v-card-title>案内状ダウンロード</v-card-title>
        <v-card-text></v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="pdfDialog = false" color="grey" text
                >キャンセル</v-btn
              >
              <v-spacer />
              <v-btn
                class="mr-2"
                @click="downloadInvitationPdf(false)"
                :disabled="isFetching"
                color="primary"
                variant="outlined"
                >選択された園児のみ</v-btn
              >
              <v-btn
                @click="downloadInvitationPdf(true)"
                :disabled="isFetching"
                color="primary"
                variant="flat"
                >全ての園児</v-btn
              >
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="memoruPdfDialog" max-width="600px">
    <!--MEMORU案内状が不要になったためコメントアウト-->
     <!--<v-card>
        <v-card-title>MEMORU案内状ダウンロード</v-card-title>
        <v-card-text></v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="pdfDialog = false" color="grey" text
                >キャンセル</v-btn
              >
              <v-spacer />
              <v-btn
                class="mr-2"
                @click="downloadInvitationPdf(false, 'memoru')"
                :disabled="isFetching"
                color="primary"
                variant="outlined"
                >選択された園児のみ</v-btn
              >
              <v-btn
                @click="downloadInvitationPdf(true, 'memoru')"
                :disabled="isFetching"
                color="primary"
                variant="flat"
                >全ての園児</v-btn
              >
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
                -->
    </v-dialog>

    <v-dialog v-model="importDialog" max-width="600px">
      <v-card>
        <v-card-title>一括インポート</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="4" align-self="center"> CSVファイル </v-col>
              <v-col cols="8" align-self="center">
                <v-file-input
                  v-model="importFile"
                  accept=".csv"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                >
                </v-file-input>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="importDialog = false" color="grey" text>
                キャンセル
              </v-btn>
              <v-spacer />
              <v-btn
                @click="importCsv"
                :disabled="!importFile"
                :loading="isFetching"
                color="primary"
                variant="flat"
              >
                インポート
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="exportChildrenDialog" max-width="600px">
      <v-card>
        <v-card-title>園児ダウンロード</v-card-title>
        <v-card-text></v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="pdfDialog = false" color="grey" text
                >キャンセル</v-btn
              >
              <v-spacer />
              <v-btn
                @click="downloadChildrenCsv(false)"
                class="mr-2"
                :disabled="isFetching"
                color="primary"
                variant="outlined"
                >選択された園児のみ</v-btn
              >
              <v-btn
                @click="downloadChildrenCsv(true)"
                :disabled="isFetching"
                color="primary"
                variant="flat"
                >全ての園児</v-btn
              >
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { childrenListModule } from "@/store/dataModules/childrenListModule";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import { NurseryChildResponse, NurseryClassResponse } from "chaild-api/lib";

import ClassPicker from "@/components/atoms/ClassPicker.vue";
import GenderPicker from "@/components/atoms/GenderPicker.vue";
import AgePicker from "@/components/atoms/AgePicker.vue";
import dayjs from "dayjs";
import ApiChildren from "@/api/ApiChildren";
import ApiImportExportStatus from "@/api/ApiImportExportStatus";
import ApiResource from "@/api/ApiResource";

interface DataType {
  isFetching: boolean;
  searchFilter: {
    isQuit: boolean;
  };
  selectedChildren: NurseryChildResponse[];
  pdfDialog: boolean;
  memoruPdfDialog: boolean;
  importFile: File | null;
  importDialog: boolean;
  exportChildrenDialog: boolean;
}

export default defineComponent({
  components: {
    ClassPicker,
    GenderPicker,
    AgePicker,
  },

  data: (): DataType => {
    return {
      isFetching: false,
      searchFilter: {
        isQuit: false,
      },
      selectedChildren: [],
      pdfDialog: false,
      memoruPdfDialog: false,
      importDialog: false,
      importFile: null,
      exportChildrenDialog: false,
    };
  },

  computed: {
    children(): NurseryChildResponse[] {
      if (this.searchFilter.isQuit) {
        // return childrenListModule.children.filter((c) => c.quitDate != null);
        return childrenListModule.children.filter((c) =>
          dayjs().isAfter(c.quitDate)
        );
      } else {
        return childrenListModule.children.filter(
          (c) => c.quitDate == null || !dayjs().isAfter(c.quitDate)
        );
      }
    },
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    classId: {
      get(): number | null {
        return childrenListModule.classId;
      },
      set(value: number | null) {
        childrenListModule.setClassId(value);
      },
    },
    firstName: {
      get(): string | null {
        return childrenListModule.firstName;
      },
      set(value: string | null) {
        childrenListModule.setFirstName(value);
      },
    },
    lastName: {
      get(): string | null {
        return childrenListModule.lastName;
      },
      set(value: string | null) {
        childrenListModule.setLastName(value);
      },
    },
    age: {
      get(): number | null {
        return childrenListModule.age;
      },
      set(value: number | null) {
        childrenListModule.setAge(value);
      },
    },
    gender: {
      get(): string | null {
        return childrenListModule.gender;
      },
      set(value: string | null) {
        childrenListModule.setGender(value);
      },
    },
    childrenTableHeaders: function () {
      return [
        { title: "園児氏名", value: "name" },
        { title: "クラス名", value: "class" },
        { title: "性別", value: "gender" },
        { title: "年齢", value: "age" },
        { title: "紐付け状況", value: "isFamilyAssociated" },
        { title: "", value: "edit" },
      ];
    },
  },

  methods: {
    async search() {
      this.isFetching = true;
      await childrenListModule.search();
      this.isFetching = false;
    },
    getClassName(child: NurseryChildResponse) {
      if (child.nurseryClasses) {
        const latest =
          child.nurseryClasses.length > 0 ? child.nurseryClasses[0] : null;
        if (latest) {
          return latest.className;
        }
      }
      return "-";
    },
    getAgeBasedonBirthdate(birthdate: string): number {
      const thisYear = dayjs();
      return thisYear.diff(birthdate, "year");
    },
    async downloadInvitationPdf(
      isAll: boolean,
      type = "home" as "home" | "memoru"
    ) {
      if (!isAll && this.selectedChildren.length == 0) {
        alert("園児が選択されていません");
        this.pdfDialog = false;
        return;
      }
      this.isFetching = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        // 案内状DLリクエスト
        const invitationResponse = await ApiChildren.getInvitationPdf({
          nurseryId,
          childIds: isAll ? [] : this.selectedChildren.map((c) => c.childId),
          type,
        }).catch(() => {
          this.isFetching = false;
        });

        if (invitationResponse) {
          // ステータス取得
          const interval = setInterval(async () => {
            this.isFetching = true;
            const exportResponse = await ApiImportExportStatus.getStatus({
              statusId: invitationResponse.statusId,
              category: invitationResponse.category,
            }).catch(() => {
              clearInterval(interval);
              this.isFetching = false;
              this.pdfDialog = false;
            });
            if (exportResponse && exportResponse.status == "success") {
              clearInterval(interval);
              window.open(exportResponse.url, "_blank");
              this.isFetching = false;
            }
          }, 1000);
        }
      }
      this.isFetching = false;
    },
    async downloadChildrenCsv(isAll: boolean) {
      if (!isAll && this.selectedChildren.length == 0) {
        alert("園児が選択されていません");
        this.exportChildrenDialog = false;
        return;
      }
      this.isFetching = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        // 園児エクスポート
        const exportResponse = await ApiChildren.exportChildrenCsv({
          nurseryId,
          childrenIds: isAll ? [] : this.selectedChildren.map((c) => c.childId),
        }).catch(() => {
          this.isFetching = false;
        });

        if (exportResponse) {
          // ステータス取得
          const interval = setInterval(async () => {
            this.isFetching = true;
            const statusResponse = await ApiImportExportStatus.getStatus({
              statusId: exportResponse.statusId,
              category: exportResponse.category,
            }).catch(() => {
              clearInterval(interval);
              this.isFetching = false;
              this.exportChildrenDialog = false;
            });
            if (statusResponse && statusResponse.status == "success") {
              clearInterval(interval);
              window.open(statusResponse.url, "_blank");
              this.isFetching = false;
              this.exportChildrenDialog = false;
            }
          }, 1000);
        }
      }
      this.isFetching = false;
    },
    async importCsv() {
      this.isFetching = true;
      const nurseryId = LocalDataService.getNurseryId();
      const imageType = "childCsv";
      if (nurseryId && this.importFile) {
        // get presigned url
        const resourceResponse = await ApiResource.getPresignedUrl({
          imageType: imageType,
          nurseryId,
          filename: this.importFile.name,
        }).catch();
        if (resourceResponse) {
          // upload
          const uploadResponse = await ApiResource.uploadFile({
            url: resourceResponse.url,
            file: this.importFile,
          }).catch();
          if (uploadResponse) {
            // import
            const importResponse = await ApiChildren.importChildrenCsv({
              nurseryId,
              filePath: resourceResponse.filePath,
            }).catch(() => {
              this.isFetching = false;
              this.importDialog = false;
            });
            if (importResponse) {
              const interval = setInterval(async () => {
                this.isFetching = true;
                const statusResponse = await ApiImportExportStatus.getStatus({
                  statusId: importResponse.statusId,
                  category: importResponse.category,
                }).catch(() => {
                  clearInterval(interval);
                  this.isFetching = false;
                  this.importDialog = false;
                });
                if (statusResponse) {
                  if (statusResponse.status == "success") {
                    clearInterval(interval);
                    this.isFetching = false;
                    this.importDialog = false;
                    alert("インポートが完了しました");
                    await this.search();
                  } else if (statusResponse.status == "failed") {
                    clearInterval(interval);
                    this.isFetching = false;
                    this.importDialog = false;
                    alert("インポートが失敗しました");
                  }
                }
              }, 1000);
            }
          }
        }
      }
      this.isFetching = false;
    },
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      this.isFetching = true;
      await classModule.listClass({
        nurseryId: nurseryId,
      });
      this.isFetching = false;
    }
    await this.search();
  },
});
</script>
